import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "~/components/icon";
import type { Cluster } from "~/types";
import { cn } from "~/utils";

import { type ClusterGroup, groupContaining } from "../grouping";

import { ClusterItem } from "./ClusterItem";
import { CLUSTER_TYPE_NAMES, type ClusterType } from "./FilterToggle";

interface ClusterListProps {
  clusters: Cluster[];
  selectedIndex: number | null;
  // Only keyboard focus force-opens a collapsed group (see below).
  selectedIndexFromKeyboard?: boolean;
  selectedClusterIds: Set<string>;
  onHover?: (index: number) => void;
  onHoverEnd?: () => void;
  onFocus?: (index: number) => void;
  onToggleSelection: (clusterId: string) => void;
  onShiftClick?: (index: number) => void;
  // Clusters the pending fleet upgrade would touch; marked in their rows.
  plannedClusterIds?: Set<string>;
  // `clusters` arrives in grouped order; a header is painted before each
  // group's first row. Indices stay flat, so selection/nav are unaffected.
  groups?: ClusterGroup[];
  onSelectGroup?: (clusterIds: string[]) => void;
  // Upgrade just this group's planned clusters (rollout view only).
  onUpgradeGroup?: (group: ClusterGroup) => void;
  // Owned by the parent so toolbar expand/collapse-all can share it.
  isGroupCollapsed?: (group: ClusterGroup) => boolean;
  onToggleGroupCollapsed?: (label: string) => void;
}

export function ClusterList({
  clusters,
  selectedIndex,
  selectedIndexFromKeyboard,
  selectedClusterIds,
  onHover,
  onHoverEnd,
  onFocus,
  onToggleSelection,
  onShiftClick,
  plannedClusterIds,
  groups,
  onSelectGroup,
  onUpgradeGroup,
  isGroupCollapsed,
  onToggleGroupCollapsed,
}: ClusterListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [preservedHeight, setPreservedHeight] = useState<number | null>(null);

  // The group holding the focused row surfaces the `⇧G` select-group hint.
  const focusedGroupStart = useMemo(() => {
    if (selectedIndex === null || !groups) return null;
    return groupContaining(groups, selectedIndex)?.startIndex ?? null;
  }, [groups, selectedIndex]);

  // Row visibility only; keyboard focus force-opens its group so nav never hides it.
  const isRowGroupCollapsed = (group: ClusterGroup) =>
    !(group.startIndex === focusedGroupStart && selectedIndexFromKeyboard) &&
    (isGroupCollapsed?.(group) ?? false);

  useEffect(() => {
    const savedHeight = sessionStorage.getItem("clusterListHeight");
    if (savedHeight) {
      setPreservedHeight(parseInt(savedHeight));
    }
  }, []);

  // When clusters load, clear the preserved height and restore scroll
  useEffect(() => {
    if (clusters.length > 0 && preservedHeight) {
      // Restore scroll position
      const scrollPos = sessionStorage.getItem("clusterListScrollPos");
      if (scrollPos) {
        window.scrollTo(0, parseInt(scrollPos));
      }

      setPreservedHeight(null);
    }
  }, [clusters, preservedHeight]);

  // Save height on every render if we have clusters
  useEffect(() => {
    if (clusters.length > 0 && listRef.current?.offsetHeight) {
      sessionStorage.setItem(
        "clusterListHeight",
        listRef.current.offsetHeight.toString(),
      );
    }
  }, [clusters]);

  // By cluster id, not position: collapsed groups can hide rows, and
  // multi-select blanks every href to "#".
  useEffect(() => {
    if (selectedIndex === null) return;
    const clusterId = clusters[selectedIndex]?.id;
    if (!clusterId) return;
    const anchor = listRef.current?.querySelector(
      `a[data-cluster-id="${CSS.escape(clusterId)}"]`,
    );
    anchor?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, clusters]);

  return (
    <div className="cluster-list-container">
      <div
        ref={listRef}
        style={preservedHeight ? { minHeight: `${preservedHeight}px` } : {}}
        className="cluster-list flex flex-col gap-1"
      >
        {clusters.map((cluster, index) => {
          const header = groups?.find((g) => g.startIndex === index);
          const group = groups ? groupContaining(groups, index) : undefined;
          const rowHidden = group ? isRowGroupCollapsed(group) : false;
          return (
            <Fragment key={cluster.id}>
              {header ? (
                <GroupHeader
                  group={header}
                  focused={header.startIndex === focusedGroupStart}
                  selectedClusterIds={selectedClusterIds}
                  onSelectGroup={onSelectGroup}
                  plannedClusterIds={plannedClusterIds}
                  onUpgradeGroup={onUpgradeGroup}
                  // Real toggle state, not rowHidden (keyboard can override display only).
                  collapsed={isGroupCollapsed?.(header) ?? false}
                  onToggleCollapsed={() =>
                    onToggleGroupCollapsed?.(header.label)
                  }
                />
              ) : null}
              {rowHidden ? null : (
                <ClusterItem
                  cluster={cluster}
                  className={cn(
                    selectedClusterIds.has(cluster.id)
                      ? "border-orange-200 bg-orange-50/80"
                      : selectedIndex === index
                        ? "border-gray-300 bg-gray-50"
                        : "opacity-60",
                    selectedIndex === null &&
                      selectedClusterIds.size === 0 &&
                      "opacity-100",
                  )}
                  onHover={() => onHover?.(index)}
                  onHoverEnd={onHoverEnd}
                  onFocus={() => onFocus?.(index)}
                  onToggleSelection={() => onToggleSelection(cluster.id)}
                  onShiftClick={() => onShiftClick?.(index)}
                  isSelected={selectedClusterIds.has(cluster.id)}
                  isHighlighted={selectedIndex === index}
                  hasMultipleSelections={selectedClusterIds.size > 0}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

// A lightweight section divider for a grouping dimension value: label, a count
// pill, an optional drift hint, then a hairline that fades out to the right.
// Clicking the header toggle-selects the whole group.
function GroupHeader({
  group,
  focused,
  selectedClusterIds,
  onSelectGroup,
  plannedClusterIds,
  onUpgradeGroup,
  collapsed,
  onToggleCollapsed,
}: {
  group: ClusterGroup;
  focused: boolean;
  selectedClusterIds: Set<string>;
  onSelectGroup?: (clusterIds: string[]) => void;
  plannedClusterIds?: Set<string>;
  onUpgradeGroup?: (group: ClusterGroup) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const behind = group.clusters.filter(
    (c) => (c.git_details?.behind_by ?? 0) > 0,
  ).length;
  const plannedCount = plannedClusterIds
    ? group.clusters.filter((c) => plannedClusterIds.has(c.id)).length
    : 0;
  const allSelected = group.clusters.every((c) => selectedClusterIds.has(c.id));
  const kindName =
    group.label !== "all" && Object.hasOwn(CLUSTER_TYPE_NAMES, group.label)
      ? CLUSTER_TYPE_NAMES[group.label as ClusterType]
      : undefined;
  return (
    <div className="mb-0.5 mt-5 flex w-full items-center gap-2.5 px-1 first:mt-1">
      <button
        type="button"
        title={collapsed ? "Expand" : "Collapse"}
        onClick={onToggleCollapsed}
        className="flex shrink-0 items-center justify-center text-gray-400 hover:text-gray-700"
      >
        <Icon name={collapsed ? "ChevronRight" : "ChevronDown"} size={14} />
      </button>
      <button
        type="button"
        title={allSelected ? "Clear group" : "Select group"}
        onClick={() => {
          onSelectGroup?.(group.clusters.map((c) => c.id));
          if (collapsed) onToggleCollapsed();
        }}
        className="group/header flex items-center gap-2.5 text-left"
      >
        <span
          className="text-xs font-semibold tracking-tight text-foreground group-hover/header:underline"
          title={kindName}
        >
          {group.label}
        </span>
        <span
          title={`${group.clusters.length} clusters`}
          className={cn(
            "rounded-full px-1.5 py-px text-[10px] font-medium tabular-nums",
            allSelected
              ? "bg-blue-100 text-blue-700"
              : "bg-slate-100 text-muted-foreground",
          )}
        >
          {group.clusters.length}
        </span>
        {/* In the rollout view the green upgrade count is the single behind
            signal; the behind_by-based one can disagree with the plan. */}
        {behind > 0 && !onUpgradeGroup ? (
          <span
            className="text-[11px] font-medium text-amber-600"
            title={`${behind} clusters behind the latest commit`}
          >
            {behind} behind
          </span>
        ) : null}
        {/* select-group hint, shown only while a row in this group is focused */}
        {focused ? (
          <kbd
            className="rounded bg-gray-100 px-1 py-0.5 text-[10px] leading-none text-gray-400"
            title="Shift+G selects this group"
          >
            ⇧G
          </kbd>
        ) : null}
      </button>
      {plannedCount > 0 && onUpgradeGroup ? (
        <button
          type="button"
          title={`Upgrade only the ${group.label} group`}
          onClick={() => onUpgradeGroup(group)}
          className="text-[11px] font-medium text-green-700 hover:underline"
        >
          upgrade {plannedCount} →
        </button>
      ) : null}
      <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
    </div>
  );
}
